import React, { Fragment, FC, useMemo, useContext, useState, useEffect } from "react";

import InfoIcon from '@pixel/assets/icons/Info'

import CircularProgress from "@pixel/core/CircularProgress";
import ContentModal from "@pixel/core/ContentModal";
import Select from "@pixel/core/Select";
import ToolTip from "@pixel/core/Tooltip";
import Typography from "@pixel/core/Typography";
import { useSnackbar } from '@pixel/core/SnackbarService'

import CandidateListContext from "../../contexts/candidate/candidateListContext";
import { useChangeStatusReq, useGetChangeStatusStates } from "../../hooks/useChangeStatus";
import { useJobDetailsEId } from "../../contexts/JobDetails";
import { useMultipleLocalizedText } from "../../contexts/Localization/localizationHooks";
import formatPluralString from "../../utils/formatPluralString";
import SubmitToHM from "./SubmitToHM";
import FloatingCircularProgress from "./FloatingCircularProgress";

interface ICallback {
  (): void;
}

interface ActionModalProps {
  open: boolean;
  onClose: ICallback;
}

const ChangeStatusModal: FC<ActionModalProps> = ({
  open = false,
  onClose = () => {},
}) => {
  const requisitionId = useJobDetailsEId()
  const snack = useSnackbar()

  const {
    COMMON_CHANGE_STATUS,
    COMMON_SELECT_NEW_STATUS,
    COMMON_CANDIDATE,
    COMMON_CANDIDATES,
    JV_APPLICANT_CANDIDATE_STATUS_UPDATED_SUCCESSFULLY,
    JV_APPLICANT_CANDIDATE_STATUS_UPDATED_ERROR,
    CANCEL,
  } = useMultipleLocalizedText()

  const {
    validatedCandidates,
    validatedCandidateIds,
  } = useValidatedCandidatesList()

  const [selValue, setSelValue] = useState(null);
  const [submitToHMOpen, setSubmitToHMOpen] = useState(false)


  const context = useContext(CandidateListContext);
  const selectedCandidates = context.selectedCandidateDetails
  const permissions = context.permissions;

  const getNextStatus = !permissions.skipWorkflowState;

  const getChangeStatusProps = useMemo(() => ({
    reqId: requisitionId,
    currentStatus: getNextStatus ? selectedCandidates[0].candidateState.EId : undefined
  }), [requisitionId, selectedCandidates, getNextStatus])
  const {
    states,
    loading: statesLoading,
    error: statesError,
  } = useGetChangeStatusStates(getChangeStatusProps)

  const selectedStatus = useMemo(() => (states || []).find((state) => state.name === selValue), [states, selValue])

  const sendChangeStatusProps = useMemo(() => ({
    appIds: validatedCandidateIds,
    workflowStateName: selValue
  }), [validatedCandidateIds, selValue])
  const {
    changeStatus,
    loading: sendLoading,
    error: sendError,
  } = useChangeStatusReq(sendChangeStatusProps)

  const statesLabelValuePairs = useMemo(() => {
    if (states.length > 0) {
      return states.map(state => ({ label: state.title, value: state.name, EId: state.EId }))
    }

    return []
  }, [states])

  useEffect(() => {
    if (states && states.length > 0) {
      setSelValue(states[0].name)
    }
  }, [states])

  const handleReject = () => {
    console.log("Cancel Status Modal");
  };
  const handleResolve = () => {
    const foundStatus = states.find(state => state.name === selValue)

    switch(foundStatus.predefinedKind) {
      case 'SUBMITTED_TO_HIRING_MANAGER':
        setSubmitToHMOpen(true)
        break
      default:
        changeStatus()
          .then(response => {
            snack.showSuccess(JV_APPLICANT_CANDIDATE_STATUS_UPDATED_SUCCESSFULLY)
            onClose()
          })
          .catch(err => {
            snack.showError(JV_APPLICANT_CANDIDATE_STATUS_UPDATED_ERROR)
          })
        break
    }

    return true
  };

  const validNamesList = useMemo(() => {
    if (validatedCandidates.length > 0) {
      return validatedCandidates.map(candidate => <Fragment key={candidate.EId}>{candidate.candidate.fullName} <br /></Fragment>)
    }

    return ''
  }, [validatedCandidates])

  return (
    <>
      <ContentModal
        id="add-candidate"
        open={open}
        onClose={onClose}
        showClose={true}
        sizeVariant="md"
        title={COMMON_CHANGE_STATUS}
        labelCloseAria="close"
        rejectActions={[{ reason: "cancel", label: CANCEL }]}
        resolveActions={[{ reason: "ok", label: COMMON_CHANGE_STATUS }]}
        onResolve={handleResolve}
        onReject={handleReject}
      >
        <FloatingCircularProgress open={sendLoading} />
        {statesLoading && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </div>
        )}
        {!statesLoading && (
          <>
            <div style={{ marginBottom: '16px' }}>
              {validatedCandidates.length > 0 && (
                <ToolTip title={<>{validNamesList}</>} placement="bottom-start">
                  <div>
                    <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                      {validNamesList.length} {' '}
                      {formatPluralString(
                        validNamesList.length,
                        COMMON_CANDIDATE,
                        COMMON_CANDIDATES,
                        )}
                      <InfoIcon sx={{ marginLeft: '5px' }} />
                    </Typography>
                  </div>
                </ToolTip>
              )}
            </div>
            <Select
              sx={{ width: 400 }}
              id="labeled"
              label={COMMON_SELECT_NEW_STATUS}
              select
              value={selValue}
              options={statesLabelValuePairs}
              onChange={(e, val) => {
                setSelValue(e.target.value)
              }}
            />
          </>
        )}
      </ContentModal>
      { submitToHMOpen && (
        <SubmitToHM
          selectedStatusEId={selectedStatus?.EId ?? ''}
          open={submitToHMOpen}
          onClose={onClose}
        />
      )}
    </>
  );
};

export function useValidatedCandidatesList() {
  const context = useContext(CandidateListContext);

  if (context === null) {
    throw 'This hook must be used within a <CandidateActionModal /> child'
  }

  return useMemo(() => {
    if (context.validatedCandidateList && context.validatedCandidateList.validApplications) {
      return {
        validatedCandidates: context.validatedCandidateList.validApplications,
        validatedCandidateIds: context.validatedCandidateList.validApplicationIds,
      }
    }

    return {
      validatedCandidates: [],
      validatedCandidateIds: [],
    }
  }, [
    context,
    context.validatedCandidateList,
    context.validatedCandidateList.validApplication,
  ])
}

export default ChangeStatusModal;
